// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// ui.ts — every screen that is not the board, as HTML strings.
//
// The results screen shows BOTH sides in full, always. It is the one moment players compare
// themselves, and a summary that only reflects you back at yourself wastes it — so each row carries
// what that side actually did (built, lost, killed, mined, salvaged) and, most importantly, what it
// BURNT: the ore it spent and never got back. In a game whose whole premise is that nothing is
// created, that last column is the scoreboard the design is really about.

import { escapeHtml } from './dom';
import { MODES, MODE_IDS, modeOf, type Mode } from './modes';
import { SEAT_COLOUR } from './palette';
import { TIERS, tierOf } from './bot';
import { UNITS } from './units';
import type { ResultRow } from './battle';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: string; tierId: string; best: number; muted: boolean }): string {
  const mode = modeOf(o.modeId);
  return `
    <section class="screen menu">
      <h1 class="title">Salient</h1>
      <p class="sub">Two thousand ore in the ground and nobody is making more. Every fight you win leaves you poorer.</p>

      <div class="picker" role="group" aria-label="Mode">
        ${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(
              MODES[id].name,
            )}</button>`,
        ).join('')}
      </div>
      <p class="blurb">${escapeHtml(mode.blurb)}</p>

      <div class="picker small" role="group" aria-label="Opponent">
        ${TIERS.map(
          (t) =>
            `<button class="chip${t.id === o.tierId ? ' on' : ''}" data-tier="${t.id}" aria-pressed="${
              t.id === o.tierId
            }">${escapeHtml(t.name)}</button>`,
        ).join('')}
      </div>
      <p class="blurb small">${escapeHtml(tierOf(o.tierId).blurb)}</p>

      <div class="menu-actions">
        <button class="btn primary big" id="playSolo">Play</button>
        <button class="btn" id="playFriends">Play with a friend</button>
        <button class="btn" id="help">How to play</button>
        <button class="btn ghost" id="about">About</button>
        <button class="btn ghost" id="mute">${o.muted ? 'Sound off' : 'Sound on'}</button>
      </div>
      ${o.best > 0 ? `<p class="best">Best tonnage in ${escapeHtml(mode.name)}: <strong>${o.best}</strong></p>` : ''}
    </section>`;
}

export function helpHtml(mode: Mode): string {
  return `
    <h2>How to play</h2>
    <p class="lede">Hold the seams, spend the ore, and remember that whatever you lose becomes the other side's money.</p>
    <ul class="rules">
      <li><b>Tap a unit</b> to select it, then <b>tap anywhere</b> to send it there. Tap an enemy to attack it.</li>
      <li><b>Press and hold</b> a unit to select the whole group around it. <b>Double-tap</b> one to select every unit of that type. <b>Drag</b> a box to select by area.</li>
      <li><b>Seams</b> pay while you stand on them for two seconds, and keep paying after you leave — but a single enemy body next to a seam freezes it instantly.</li>
      <li><b>Nothing heals.</b> Armour spalls off permanently every sixth hit, so the second fight is never the same as the first.</li>
      <li><b>Every death leaves a wreck</b> worth half the unit, drained by whoever stands on it alone — and it blocks line of fire until the ore runs out. Collecting your income destroys your cover.</li>
      <li><b>Anvils and wrecks block shots.</b> Harriers and Arbalests do not. Park an Anvil in front of your own Arbalest and it will not fire.</li>
    </ul>
    <h3>The three units</h3>
    <ul class="units">
      ${UNITS.map(
        (u) =>
          `<li><b>${escapeHtml(u.name)}</b> <span class="cost">${u.cost} ore</span><br><span class="role">${escapeHtml(
            u.role,
          )}</span></li>`,
      ).join('')}
    </ul>
    <p class="modeline"><b>${escapeHtml(mode.name)}:</b> ${escapeHtml(mode.rule)}</p>
    <button class="btn primary" id="tut">Play the tutorial</button>
    <button class="btn sheet-close">Close</button>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Salient</h2>
    <p>A pocket real-time strategy game: one screen, no camera, three units, and an economy made
       mostly of your own casualties. Built for a phone first.</p>
    <p>Both players run the identical simulation from the same seed and exchange only their orders,
       so nothing about the battle is ever stored anywhere. Playing with a friend uses a
       peer-to-peer connection brokered by a public signalling relay to introduce the two browsers to
       each other; after that the traffic goes directly between you. No accounts, no cookies, no
       game server, and nothing about your play is recorded.</p>
    <p>Page views are counted anonymously with Cloudflare Web Analytics, which sets no cookies.</p>
    <p class="credit">${ATTRIB}</p>
    <button class="btn sheet-close">Close</button>`;
}

const ENDINGS: Record<string, string> = {
  forge: 'The Forge went down.',
  broken: 'One side ran out of bodies and money at the same time.',
  bank: 'Six hundred ore banked out of the Foundry.',
  tonnage: 'The clock ran out — adjudicated on tonnage.',
};

export function resultsHtml(o: {
  rows: ResultRow[];
  mode: Mode;
  round: number;
  ending: string;
  fault: string | null;
  tally: number[];
  worldBurnt: number;
}): string {
  const winner = o.rows.find((r) => r.won);
  const head =
    o.fault === 'walkover'
      ? 'Your opponent left'
      : o.fault === 'build'
        ? 'Different builds'
        : winner
          ? `${escapeHtml(winner.name)} holds the field`
          : 'A draw';
  const why =
    o.fault === 'walkover'
      ? 'They stopped answering, so the round goes to you.'
      : o.fault === 'build'
        ? 'Someone here is on an older build of Salient — reload and the room will work again.'
        : (ENDINGS[o.ending] ?? '');

  return `
    <h2 class="rhead">${head}</h2>
    <p class="rsub">${escapeHtml(o.mode.name)} · round ${o.round}${why ? ` · ${escapeHtml(why)}` : ''}</p>
    <p class="rburn">Ore burnt and never recovered: <strong>${o.worldBurnt}</strong></p>
    <table class="rtable">
      <thead><tr><th>Side</th><th>Tonnage</th><th>Built</th><th>Lost</th><th>Killed</th><th>Mined</th><th>Salvaged</th><th>Burnt</th></tr></thead>
      <tbody>
      ${o.rows
        .map(
          (r) => `<tr class="${r.won ? 'won' : ''}">
            <td class="rname"><i style="--seat:${SEAT_COLOUR[r.seat]}"></i>${escapeHtml(r.name)}${
              r.isSelf ? ' <span class="you">you</span>' : ''
            }${r.isBot ? ' <span class="botmark">bot</span>' : ''}</td>
            <td><b>${r.tonnage}</b></td><td>${r.built}</td><td>${r.lost}</td><td>${r.kills}</td>
            <td>${r.mined}</td><td>${r.salvaged}</td><td>${r.burnt}</td>
          </tr>`,
        )
        .join('')}
      </tbody>
    </table>
    ${
      o.tally.length === 2 && o.tally[0] + o.tally[1] > 0
        ? `<p class="tally">Match so far: <b>${o.tally[0]}</b> – <b>${o.tally[1]}</b></p>`
        : ''
    }`;
}

export const waitingHtml = (name: string): string =>
  `<span class="spinner"></span> Waiting for ${escapeHtml(name)}…`;
